import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import PageHeader from "./PageHeader";

// Jednoduché exportní utility (bez závislosti na externích knihovnách)
function exportToCSV(rows, settings) {
  const leadFee = settings.leadFee ?? 100;
  const comsCoef = settings.comsCoef ?? 1.0;

  const headers = [
    "Firma", "IČO", "Tipař", "BMSL", "FC (%)", "Typ",
    "Datum zobchodování", "Coms FEE (Kč)", "Odměna tipař (Kč)"
  ];

  const lines = [headers.join(";")];
  rows.forEach(r => {
    const reward = leadFee + (r.comsFee ? r.comsFee * comsCoef : 0);
    lines.push([
      r.name,
      r.ico,
      r.tipar,
      r.bmsl,
      r.fc,
      r.type === "upsell" ? "Upsell" : "Nový obchod",
      r.closedDate || "",
      r.comsFee != null ? r.comsFee : "",
      reward.toFixed(0)
    ].map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";"));
  });

  const BOM = "\uFEFF";
  const blob = new Blob([BOM + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `lead-ba-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const FC_OPTIONS = [
  { value: "", label: "Vše" },
  { value: "100", label: "100 % (výhra)" },
  { value: "75", label: "75 %" },
  { value: "50", label: "50 %" },
  { value: "25", label: "25 %" },
  { value: "10", label: "10 %" },
  { value: "0", label: "0 %" },
];

export default function LeadBaAdmin({ isDarkMode, currentUserId, userRole }) {
  const navigate = useNavigate();

  // Nastavení
  const [settings, setSettings] = useState({
    enabled: false,
    tipari: [],
    leadFee: 100,
    comsCoef: 1.0,
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState("");

  // Tipař management
  const [newTipar, setNewTipar] = useState("");

  // Statistiky
  const [rows, setRows] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  // Filtry
  const [filterFc, setFilterFc] = useState("");
  const [filterTipar, setFilterTipar] = useState("");
  const [filterType, setFilterType] = useState(""); // "" | "new" | "upsell"

  // Coms FEE editace
  const [editingComsFee, setEditingComsFee] = useState({}); // { [companyId]: value }
  const [comsFeeMsg, setComsFeeMsg] = useState({});

  const uid = currentUserId || localStorage.getItem("lastUserId") || "";

  // --- Načtení nastavení ---
  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await fetch(`${API_URL}/leadba/settings?userId=${uid}`);
      if (res.ok) setSettings(await res.json());
    } catch (_) {}
    setSettingsLoading(false);
  }, [uid]);

  // --- Načtení statistik ---
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await fetch(`${API_URL}/leadba/stats?userId=${uid}`);
      if (!res.ok) throw new Error();
      const data = await res.json();

      const mapped = [];
      (data.companies || []).forEach(c => {
        mapped.push({
          id: c._id,
          name: c.name,
          ico: c.ico,
          bmsl: c.bmsl,
          fc: c.forecast,
          tipar: c.leadBaTipar || "—",
          closedDate: c.closedDate || c.zobchodovano || null,
          comsFee: c.leadBaComsFee,
          type: "new",
        });
      });
      (data.upsells || []).forEach(u => {
        mapped.push({
          id: u._id,
          name: u.companyName || u.name,
          ico: u.ico || "",
          bmsl: u.bmsl,
          fc: u.forecast,
          tipar: "—",
          closedDate: u.closedDate || null,
          comsFee: null,
          type: "upsell",
        });
      });
      setRows(mapped);
    } catch (_) {}
    setStatsLoading(false);
  }, [uid]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (settings.enabled) loadStats();
  }, [settings.enabled, loadStats]);

  // --- Uložení nastavení ---
  const saveSettings = async (partial = {}) => {
    setSettingsSaving(true);
    try {
      const body = { ...settings, ...partial };
      const res = await fetch(`${API_URL}/leadba/settings?userId=${uid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const saved = await res.json();
        setSettings(saved);
        setSettingsMsg("Uloženo ✓");
        setTimeout(() => setSettingsMsg(""), 2500);
      } else {
        setSettingsMsg("Chyba při ukládání");
      }
    } catch (_) {
      setSettingsMsg("Chyba při ukládání");
    }
    setSettingsSaving(false);
  };

  const handleToggleEnabled = () => saveSettings({ enabled: !settings.enabled });

  const handleAddTipar = () => {
    const t = newTipar.trim();
    if (!t || settings.tipari.includes(t)) return;
    const updated = [...settings.tipari, t];
    setSettings(s => ({ ...s, tipari: updated }));
    setNewTipar("");
    saveSettings({ tipari: updated });
  };

  const handleRemoveTipar = (name) => {
    const updated = settings.tipari.filter(t => t !== name);
    setSettings(s => ({ ...s, tipari: updated }));
    saveSettings({ tipari: updated });
  };

  // --- Uložení Coms FEE ---
  const saveComsFee = async (rowId, value) => {
    try {
      const res = await fetch(`${API_URL}/leadba/company/${rowId}/comsfee?userId=${uid}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comsFee: value === "" ? null : Number(value) }),
      });
      if (res.ok) {
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, comsFee: value === "" ? null : Number(value) } : r));
        setComsFeeMsg(prev => ({ ...prev, [rowId]: "✓" }));
        setTimeout(() => setComsFeeMsg(prev => ({ ...prev, [rowId]: "" })), 1500);
      }
    } catch (_) {}
  };

  // --- Filtrování ---
  const filteredRows = rows.filter(r => {
    if (filterFc && String(r.fc) !== filterFc) return false;
    if (filterTipar && r.tipar !== filterTipar) return false;
    if (filterType === "new" && r.type !== "new") return false;
    if (filterType === "upsell" && r.type !== "upsell") return false;
    return true;
  });

  // Unikátní tipáři pro filtr
  const uniqueTipari = [...new Set(rows.map(r => r.tipar).filter(t => t && t !== "—"))];

  // Součty
  const totalLeads = filteredRows.filter(r => r.type === "new").length;
  const totalWins = filteredRows.filter(r => String(r.fc) === "100").length;
  const totalBmsl = filteredRows.reduce((s, r) => s + (parseFloat(r.bmsl) || 0), 0);
  const totalReward = filteredRows.reduce((s, r) => {
    const fee = settings.leadFee ?? 100;
    const coef = settings.comsCoef ?? 1.0;
    return s + fee + (r.comsFee ? r.comsFee * coef : 0);
  }, 0);

  // Styly
  const bg = isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900";
  const card = isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const input = isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-white border-gray-300 text-gray-900";
  const th = isDarkMode ? "bg-gray-700 text-gray-200" : "bg-blue-50 text-blue-900";
  const trHover = isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50";

  if (userRole !== "admin") return null;

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-800 dark:to-gray-900 py-10`}>
      <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
        <PageHeader
          title="Lead BA"
          isDarkMode={isDarkMode}
          actions={
            <button
              onClick={() => navigate("/users")}
              className="px-4 py-2 rounded-lg bg-gray-500 hover:bg-gray-600 text-white font-semibold shadow-md transition"
            >
              ← Admin panel
            </button>
          }
        />

        {settingsLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
          </div>
        ) : (
          <>
            {/* === NASTAVENÍ === */}
            <div className={`${card} border rounded-xl p-6 mb-6 shadow`}>
              <h2 className="text-lg font-bold mb-4">⚙️ Nastavení Lead BA</h2>

              {/* Toggle */}
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={handleToggleEnabled}
                  disabled={settingsSaving}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none ${
                    settings.enabled ? "bg-green-500" : "bg-gray-400"
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                      settings.enabled ? "translate-x-7" : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="font-semibold text-base">
                  {settings.enabled ? "✅ Lead BA zapnuto" : "⭕ Lead BA vypnuto"}
                </span>
                {settingsMsg && (
                  <span className="text-sm text-green-600 dark:text-green-400">{settingsMsg}</span>
                )}
              </div>

              {settings.enabled && (
                <>
                  {/* Koeficienty */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Odměna za každý lead (Kč)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={settings.leadFee}
                        onChange={e => setSettings(s => ({ ...s, leadFee: Number(e.target.value) }))}
                        onBlur={() => saveSettings()}
                        className={`w-full border rounded-lg px-3 py-2 text-sm ${input}`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Koeficient Coms FEE (× Coms FEE = přídavek k odměně)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={settings.comsCoef}
                        onChange={e => setSettings(s => ({ ...s, comsCoef: Number(e.target.value) }))}
                        onBlur={() => saveSettings()}
                        className={`w-full border rounded-lg px-3 py-2 text-sm ${input}`}
                      />
                    </div>
                  </div>

                  {/* Tipáři */}
                  <div>
                    <h3 className="font-semibold mb-2">👥 Seznam tipařů</h3>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={newTipar}
                        onChange={e => setNewTipar(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleAddTipar()}
                        placeholder="Jméno tipaře..."
                        className={`flex-1 border rounded-lg px-3 py-2 text-sm ${input}`}
                      />
                      <button
                        onClick={handleAddTipar}
                        disabled={!newTipar.trim()}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm font-semibold"
                      >
                        + Přidat
                      </button>
                    </div>
                    {settings.tipari.length === 0 ? (
                      <p className="text-sm text-gray-400">Zatím žádní tipáři.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {settings.tipari.map(t => (
                          <span
                            key={t}
                            className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${
                              isDarkMode
                                ? "bg-blue-900/40 border-blue-700 text-blue-200"
                                : "bg-blue-50 border-blue-200 text-blue-800"
                            }`}
                          >
                            {t}
                            <button
                              onClick={() => handleRemoveTipar(t)}
                              className="ml-1 text-red-400 hover:text-red-600 font-bold text-xs"
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* === STATISTIKY === */}
            {settings.enabled && (
              <div className={`${card} border rounded-xl p-6 shadow`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h2 className="text-lg font-bold">📊 Statistiky Lead BA</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={loadStats}
                      className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                    >
                      🔄 Obnovit
                    </button>
                    <button
                      onClick={() => exportToCSV(filteredRows, settings)}
                      className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold"
                    >
                      📥 Export CSV
                    </button>
                  </div>
                </div>

                {/* Filtry */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">FC (stav případu)</label>
                    <select
                      value={filterFc}
                      onChange={e => setFilterFc(e.target.value)}
                      className={`w-full border rounded-lg px-3 py-2 text-sm ${input}`}
                    >
                      {FC_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Tipař</label>
                    <select
                      value={filterTipar}
                      onChange={e => setFilterTipar(e.target.value)}
                      className={`w-full border rounded-lg px-3 py-2 text-sm ${input}`}
                    >
                      <option value="">Všichni</option>
                      {uniqueTipari.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Typ</label>
                    <select
                      value={filterType}
                      onChange={e => setFilterType(e.target.value)}
                      className={`w-full border rounded-lg px-3 py-2 text-sm ${input}`}
                    >
                      <option value="">Vše</option>
                      <option value="new">Nový obchod</option>
                      <option value="upsell">Upsell</option>
                    </select>
                  </div>
                </div>

                {/* Souhrn */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {[
                    { label: "Leadů celkem", value: totalLeads },
                    { label: "Výher (FC 100%)", value: totalWins },
                    { label: "BMSL celkem", value: Math.round(totalBmsl).toLocaleString("cs-CZ") + " Kč" },
                    { label: "Odměny tipařů", value: Math.round(totalReward).toLocaleString("cs-CZ") + " Kč" },
                  ].map(s => (
                    <div
                      key={s.label}
                      className={`rounded-lg p-3 border text-center ${isDarkMode ? "bg-gray-700/50 border-gray-600" : "bg-blue-50 border-blue-200"}`}
                    >
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{s.label}</div>
                      <div className="text-lg font-bold">{s.value}</div>
                    </div>
                  ))}
                </div>

                {/* Tabulka */}
                {statsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent" />
                  </div>
                ) : filteredRows.length === 0 ? (
                  <p className="text-center py-8 text-gray-400">Žádné záznamy.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className={th}>
                          <th className="py-2 px-3 text-left font-semibold">Firma</th>
                          <th className="py-2 px-3 text-left font-semibold">IČO</th>
                          <th className="py-2 px-3 text-left font-semibold">Tipař</th>
                          <th className="py-2 px-3 text-left font-semibold">BMSL</th>
                          <th className="py-2 px-3 text-left font-semibold">FC</th>
                          <th className="py-2 px-3 text-left font-semibold">Typ</th>
                          <th className="py-2 px-3 text-left font-semibold">Zobchodováno</th>
                          <th className="py-2 px-3 text-left font-semibold">Coms FEE</th>
                          <th className="py-2 px-3 text-left font-semibold">Odměna tipař</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map(r => {
                          const reward = (settings.leadFee ?? 100) + (r.comsFee ? r.comsFee * (settings.comsCoef ?? 1.0) : 0);
                          const fcColor = String(r.fc) === "100"
                            ? "text-green-600 dark:text-green-400 font-bold"
                            : Number(r.fc) >= 50
                            ? "text-yellow-600 dark:text-yellow-400"
                            : "text-red-500 dark:text-red-400";

                          const editVal = editingComsFee[r.id] !== undefined
                            ? editingComsFee[r.id]
                            : (r.comsFee != null ? String(r.comsFee) : "");

                          return (
                            <tr key={r.id + r.type} className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-100"} ${trHover}`}>
                              <td className="py-2 px-3 font-medium">{r.name}</td>
                              <td className="py-2 px-3 text-gray-400">{r.ico || "—"}</td>
                              <td className="py-2 px-3">{r.tipar}</td>
                              <td className="py-2 px-3">
                                {r.bmsl ? Number(r.bmsl).toLocaleString("cs-CZ") + " Kč" : "—"}
                              </td>
                              <td className={`py-2 px-3 ${fcColor}`}>
                                {r.fc != null && r.fc !== "" ? `${r.fc} %` : "—"}
                              </td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  r.type === "upsell"
                                    ? isDarkMode ? "bg-purple-900/50 text-purple-300" : "bg-purple-100 text-purple-700"
                                    : isDarkMode ? "bg-blue-900/50 text-blue-300" : "bg-blue-100 text-blue-700"
                                }`}>
                                  {r.type === "upsell" ? "Upsell" : "Nový obchod"}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-gray-400">
                                {r.closedDate
                                  ? new Date(r.closedDate).toLocaleDateString("cs-CZ")
                                  : "—"}
                              </td>
                              <td className="py-2 px-3">
                                {r.type === "new" ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="number"
                                      min="0"
                                      value={editVal}
                                      onChange={e =>
                                        setEditingComsFee(prev => ({ ...prev, [r.id]: e.target.value }))
                                      }
                                      onBlur={e => {
                                        saveComsFee(r.id, e.target.value);
                                        setEditingComsFee(prev => {
                                          const next = { ...prev };
                                          delete next[r.id];
                                          return next;
                                        });
                                      }}
                                      onKeyDown={e => {
                                        if (e.key === "Enter") {
                                          saveComsFee(r.id, e.target.value);
                                          setEditingComsFee(prev => {
                                            const next = { ...prev };
                                            delete next[r.id];
                                            return next;
                                          });
                                        }
                                      }}
                                      placeholder="0"
                                      className={`w-24 border rounded px-2 py-1 text-sm ${input}`}
                                    />
                                    {comsFeeMsg[r.id] && (
                                      <span className="text-xs text-green-500">{comsFeeMsg[r.id]}</span>
                                    )}
                                  </div>
                                ) : "—"}
                              </td>
                              <td className="py-2 px-3 font-semibold text-green-600 dark:text-green-400">
                                {Math.round(reward).toLocaleString("cs-CZ")} Kč
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <p className="text-xs text-gray-400 mt-3">
                  Odměna = {settings.leadFee ?? 100} Kč (za lead) + Coms FEE × {settings.comsCoef ?? 1.0} (koeficient)
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
